import React, { useState, useEffect, useContext, useRef } from "react";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faTimes,
  faTags,
  faBook,
  faBookmark,
} from "@fortawesome/free-solid-svg-icons";
import UserContext from "../../../contexts/UserContext";
import apiService from "../../../ApiService";
import AIAssistButton from "./AIAssistButton";
import MarkdownRenderer from "./MarkdownRenderer";
import "./NoteManager.css";
import "./AIFormEnhancements.css";

const NoteManager = () => {
  const { user } = useContext(UserContext);
  const userId = user?.userId;
  const [notes, setNotes] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterSubject, setFilterSubject] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [formData, setFormData] = useState({
    subject_id: "",
    source_id: "",
    note_title: "",
    note_content: "",
    tags: "",
  });
  const [aiLoading, setAiLoading] = useState(false);
  const noteTextareaRef = useRef(null);

  useEffect(() => {
    fetchSubjects();
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (formData.subject_id) {
      fetchSourcesForSubject(formData.subject_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.subject_id]);

  const fetchSubjects = async () => {
    try {
      const response = await apiService.getSubjects(userId);
      setSubjects(response.subjects || []);
    } catch (error) {
      console.error("Error fetching subjects:", error);
    }
  };

  const fetchSourcesForSubject = async (subjectId) => {
    try {
      const response = await apiService.getSources(userId, {
        subject_id: subjectId,
      });
      setSources(response.sources || []);
    } catch (error) {
      console.error("Error fetching sources:", error);
      setSources([]);
    }
  };

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const response = await apiService.getNotes(userId, {
        search: searchTerm || undefined,
        subject_id: filterSubject || undefined,
      });
      setNotes(response.notes || []);
    } catch (error) {
      console.error("Error fetching notes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchNotes();
  };

  useEffect(() => {
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSubject]);

  const openCreateModal = () => {
    setEditingNote(null);
    const firstSubjectId = subjects.length > 0 ? subjects[0].subject_id : "";
    setFormData({
      subject_id: firstSubjectId,
      source_id: "",
      note_title: "",
      note_content: "",
      tags: "",
    });
    setAiLoading(false);
    // Fetch sources for the first subject
    if (firstSubjectId) {
      fetchSourcesForSubject(firstSubjectId);
    }
    setShowModal(true);
  };

  const openEditModal = (note) => {
    setEditingNote(note);
    setFormData({
      subject_id: note.subject_id,
      source_id: note.source_id || "",
      note_title: note.title || "",
      note_content: note.content || "",
      tags: note.tags || "",
    });
    setAiLoading(false);
    // Fetch sources for the note's subject
    if (note.subject_id) {
      fetchSourcesForSubject(note.subject_id);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingNote(null);
    setFormData({
      subject_id: "",
      source_id: "",
      note_title: "",
      note_content: "",
      tags: "",
    });
    setSources([]);
    setAiLoading(false);
  };

  const handleOverlayMouseDown = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const handleGenerateNoteContent = async () => {
    if (!formData.note_title.trim()) {
      alert("Please enter a note title first");
      return;
    }

    if (!formData.subject_id) {
      alert("Please select a subject first");
      return;
    }

    const textarea = noteTextareaRef.current;
    const selectionStart = textarea?.selectionStart ?? 0;
    const selectionEnd = textarea?.selectionEnd ?? 0;
    if (textarea && selectionEnd > selectionStart) {
      await handleRewriteNoteSelection();
      return;
    }

    setAiLoading(true);
    try {
      const selectedSubject = subjects.find(
        (s) => s.subject_id.toString() === formData.subject_id.toString(),
      );
      const selectedSource = sources.find(
        (s) => s.source_id.toString() === formData.source_id.toString(),
      );

      console.log("Generating note content for:", {
        title: formData.note_title,
        subject: selectedSubject?.subject_name,
        source: selectedSource?.source_title || selectedSource?.source_name,
      });

      const response = await apiService.generateNoteContent(
        formData.note_title,
        selectedSubject?.subject_name || "Unknown",
        selectedSource?.source_title || selectedSource?.source_name,
      );

      console.log("AI Response:", response);

      if (response.success) {
        console.log("Setting note content:", response.content);
        setFormData((prev) => ({ ...prev, note_content: response.content }));
      } else {
        alert("Failed to generate note content. Please try again.");
      }
    } catch (error) {
      console.error("AI generation error:", error);
      alert("Failed to generate note content. Please try again.");
    } finally {
      setAiLoading(false);
    }
  };

  const handleRewriteNoteSelection = async () => {
    const textarea = noteTextareaRef.current;
    if (!textarea) return;

    const selectionStart = textarea.selectionStart ?? 0;
    const selectionEnd = textarea.selectionEnd ?? 0;
    if (selectionEnd <= selectionStart) {
      alert("Select the text you want to rewrite.");
      return;
    }

    const selectedText = textarea.value
      .slice(selectionStart, selectionEnd)
      .trim();
    if (!selectedText) {
      alert("Select the text you want to rewrite.");
      return;
    }

    const selectedSubject = subjects.find(
      (s) => s.subject_id.toString() === formData.subject_id.toString(),
    );

    setAiLoading(true);
    try {
      const response = await apiService.rewriteSelectedText(
        selectedText,
        selectedSubject?.subject_name || "this subject",
      );

      if (response.success) {
        const before = textarea.value.slice(0, selectionStart);
        const after = textarea.value.slice(selectionEnd);
        const nextValue = `${before}${response.content}${after}`;

        setFormData((prev) => ({ ...prev, note_content: nextValue }));
        setTimeout(() => {
          textarea.focus();
          const caret = selectionStart + response.content.length;
          textarea.setSelectionRange(caret, caret);
        }, 0);
      } else {
        alert("Failed to rewrite selection. Please try again.");
      }
    } catch (error) {
      console.error("AI rewrite error:", error);
      alert("Failed to rewrite selection. Please try again.");
    } finally {
      setAiLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.ctrlKey && e.code === "Space") {
      e.preventDefault();
      handleGenerateNoteContent();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Map frontend field names to API field names
      const payload = {
        user_id: userId,
        subject_id: formData.subject_id,
        source_id: formData.source_id || null,
        title: formData.note_title,
        content: formData.note_content,
        tags: formData.tags,
      };

      console.log("Submitting note with data:", payload);

      if (editingNote) {
        await apiService.updateNote(userId, editingNote.note_id, payload);
      } else {
        await apiService.createNote(userId, payload);
      }
      closeModal();
      fetchNotes();
    } catch (error) {
      console.error("Error saving note:", error);
      alert("Failed to save note. Please try again.");
    }
  };

  const handleDelete = async (noteId, noteTitle) => {
    if (window.confirm(`Are you sure you want to delete "${noteTitle}"?`)) {
      try {
        await apiService.deleteNote(userId, noteId);
        fetchNotes();
      } catch (error) {
        console.error("Error deleting note:", error);
        alert("Failed to delete note. Please try again.");
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Not available";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "Invalid date";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const renderTags = (tagsStr) => {
    if (!tagsStr) return null;
    const tags = tagsStr
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t);
    return tags.map((tag, index) => (
      <span key={index} className="tag-badge">
        #{tag}
      </span>
    ));
  };

  return (
    <div className="note-manager">
      {/* Header Actions */}
      <div className="manager-header">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-wrapper">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <button type="submit" className="search-btn">
            Search
          </button>
        </form>
        <select
          value={filterSubject}
          onChange={(e) => setFilterSubject(e.target.value)}
          className="filter-select"
        >
          <option value="">All Subjects</option>
          {subjects.map((subject) => (
            <option key={subject.subject_id} value={subject.subject_id}>
              {subject.subject_name}
            </option>
          ))}
        </select>
        <button onClick={openCreateModal} className="create-btn">
          <FontAwesomeIcon icon={faPlus} /> Add Note
        </button>
      </div>

      {/* Notes Grid */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading notes...</p>
        </div>
      ) : notes.length === 0 ? (
        <div className="empty-notes">
          <div className="empty-icon">📝</div>
          <h3>No notes found</h3>
          <p>Create your first note to start documenting your learning!</p>
          <button onClick={openCreateModal} className="create-btn">
            <FontAwesomeIcon icon={faPlus} /> Create Note
          </button>
        </div>
      ) : (
        <div className="notes-grid">
          {notes.map((note) => (
            <div key={note.note_id} className="note-card">
              <div className="card-header">
                <h3>{note.title}</h3>
                <div className="card-actions">
                  <button
                    onClick={() => openEditModal(note)}
                    className="action-btn edit-btn"
                    title="Edit"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button
                    onClick={() => handleDelete(note.note_id, note.title)}
                    className="action-btn delete-btn"
                    title="Delete"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>

              <div className="note-meta">
                <div className="meta-item">
                  <FontAwesomeIcon icon={faBook} />
                  <span>{note.subject_name}</span>
                </div>
                {note.source_name && (
                  <div className="meta-item">
                    <FontAwesomeIcon icon={faBookmark} />
                    <span>{note.source_name}</span>
                  </div>
                )}
              </div>

              {note.content && (
                <MarkdownRenderer
                  content={note.content}
                  className="card-content"
                  preview
                  previewLength={200}
                />
              )}

              {note.tags && (
                <div className="card-tags">
                  <FontAwesomeIcon icon={faTags} />
                  {renderTags(note.tags)}
                </div>
              )}

              <div className="card-footer">
                <span className="date-text">
                  {note.created_date
                    ? `Created: ${formatDate(note.created_date)}`
                    : note.modified_date
                      ? `Updated: ${formatDate(note.modified_date)}`
                      : "No date available"}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal &&
        ReactDOM.createPortal(
          <div className="modal-overlay" onMouseDown={handleOverlayMouseDown}>
            <div
              className="modal-content note-modal"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>{editingNote ? "Edit Note" : "Create New Note"}</h2>
                <button onClick={closeModal} className="close-btn">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="modal-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="subject_id">
                      Subject <span className="required">*</span>
                    </label>
                    <select
                      id="subject_id"
                      value={formData.subject_id}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          subject_id: e.target.value,
                          source_id: "",
                        })
                      }
                      required
                    >
                      {subjects.length === 0 ? (
                        <option value="">No subjects available</option>
                      ) : (
                        subjects.map((subject) => (
                          <option
                            key={subject.subject_id}
                            value={subject.subject_id}
                          >
                            {subject.subject_name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="source_id">Source (Optional)</label>
                    <select
                      id="source_id"
                      value={formData.source_id}
                      onChange={(e) =>
                        setFormData({ ...formData, source_id: e.target.value })
                      }
                    >
                      <option value="">No specific source</option>
                      {sources.map((source) => (
                        <option key={source.source_id} value={source.source_id}>
                          {source.source_title || source.source_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="note_title">
                    Note Title <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="note_title"
                    value={formData.note_title}
                    onChange={(e) =>
                      setFormData({ ...formData, note_title: e.target.value })
                    }
                    required
                    maxLength="200"
                    placeholder="e.g., useState Hook Explained"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="note_content">
                    Note Content
                    <AIAssistButton
                      onGenerate={handleGenerateNoteContent}
                      loading={aiLoading}
                      size="small"
                      tooltip="Generate content with AI (Ctrl+Space)"
                    />
                  </label>
                  <textarea
                    ref={noteTextareaRef}
                    id="note_content"
                    value={formData.note_content}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        note_content: e.target.value,
                      }))
                    }
                    onKeyDown={handleKeyDown}
                    rows="10"
                    placeholder="Write your notes here... (Ctrl+Space for AI assist)"
                    disabled={aiLoading}
                  ></textarea>
                </div>

                <div className="form-group">
                  <label htmlFor="tags">
                    Tags <small>(comma-separated)</small>
                  </label>
                  <input
                    type="text"
                    id="tags"
                    value={formData.tags}
                    onChange={(e) =>
                      setFormData({ ...formData, tags: e.target.value })
                    }
                    placeholder="e.g., react, hooks, state"
                  />
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={subjects.length === 0}
                  >
                    {editingNote ? "Update" : "Create"} Note
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default NoteManager;
